<template>
  
  <div v-if="!chartTypeCheck.includes(charttype)">CHART TYPE ERROR</div>
  
  <div v-else-if="charttype === 'pie_description' || this.charttype === 'pie_description_percentage'" :class="`chart-pie-description__container`">
    <div class="chart-pie__description">
      <p>{{ charttitle }}</p>
      
      <div v-for="series in seriesData" :key="series.name" class="chart-pie__description-wrapper">
        <div v-for="data in series.data" :key="data.name" class="chart-pie__description-line">
          <div class="chart-pie__description-color">
            <svg width="18" height="18">
              <circle cx="9" cy="9" r="9" :fill="data.colour" />
            </svg>
          </div>
          <div class="chart-pie__description-name">{{ data.name }}</div>
          <div class="chart-pie__description-number">{{ data.y }}</div>
        </div>
      </div>
    </div>
    <figure>
      <div
        :class="`chart-${chartTypeClass}__chart`"
        ref="chart"
        aria-hidden="true"
      ></div>
      <figcaption class="sr-only">
        {{ charttitle }} - {{ categories }} - {{ seriesData }}
      </figcaption>
    </figure>
  </div>
  
  <figure v-else :class="`chart-${chartTypeClass}__container`">
    <div
      :class="`chart-${chartTypeClass}__chart`"
      ref="chart"
      aria-hidden="true"
    ></div>
    <figcaption class="sr-only">
      {{ charttitle }} - {{ categories }} - {{ seriesData }}
    </figcaption>
  </figure>
</template>

<script>
import axios from "axios";

export default {
  name: "Chart",
  props: {
    csvfilepath: { type: String, required: true },
    charttitle: { type: String, required: false },
    charttype: { type: String, required: true },
    language: { type: String, required: true }, // Language prop from CMS
  },
  data() {
    return {
      chart: null,
      rawCsvData: null,
      categories: [],
      seriesData: [],
      chartSeries: {
        stacking: null,
      },
      chartTypeClass: null,
      chartTypeCheck: [
        "column_stacked",
        "column_grouped", 
        "column_percentage_stacked", 
        "column_grouped_percentage_line", 
        "pie_only",
        "pie_description",
        "pie_only_percentage",
        "pie_description_percentage",
        "bar_stacked", 
        "bar_percentage_stacked", 
        "bar_non_stacked"
      ],
      colours: [
        "#ffd91b", // yellow
        "#0062b0", // blue
        "#000000", // black
        "#702f8a", // purple
        "#8cccff", // lt blue
        "#7AB82A", // green
        "#F37421", // orange
        "#0099CC", // lt blue 2
        "#DA109A", // pink
        "#666666", // grey
      ],
    };
  },
  mounted() {
    this.ensureHighcharts(() => {
      this.settingTemplateClassess;
      this.setHichartsOptions();
    });
  },
  computed: {
    settingTemplateClassess() {
      if (this.charttype.includes("column")) {
        this.chartTypeClass = "column";
      } else if (this.charttype.includes("pie")) {
        this.chartTypeClass = "pie";
      } else {
        this.chartTypeClass = "bar";
      }
    },
  },
  methods: {
    //create <script>
    ensureHighcharts(callback) {
      if (typeof Highcharts !== "undefined") {
        callback();
        console.log("Highcharts already loaded");
      } else {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/highcharts/11.4.8/highcharts.min.js";
        document.head.appendChild(script);
        script.onload = callback;
        console.log("Highcharts script created and loaded...");
      }
    },

    // Chart variables
    setHichartsOptions() {
      // Set tooltip and value format defaults
      this.tooltipFormat = "{series.name}: <b>{point.y}</b>. ";
      this.valueFormat = "<b>{point.y}</b>";

      // 'column_grouped_percentage_line'
      if (this.charttype === "column_grouped_percentage_line") {
        if (this.language == "fr") {
          this.valueFormatSecondAxis = "<b>{point.y} %</b>";
        } else {
          this.valueFormatSecondAxis = "<b>{point.y}%</b>";
        }
      }
      // 'column_stacked' and 'bar_stacked'
      else if (
        this.charttype === "column_stacked" ||
        this.charttype === "bar_stacked"
      ) {
        this.chartSeries.stacking = "stacked";
      }
      // 'column_percentage_stacked' or 'bar_percentage_stacked'
      else if (
        this.charttype === "column_percentage_stacked" || 
        this.charttype === "bar_percentage_stacked"
      ) {
        this.chartSeries.stacking = "percent";
        if (this.language == "fr") {
          this.tooltipFormat = "{series.name}: <b>{point.percentage:.1f} %</b> ({point.y}). ";
          this.valueFormat = "<b>{point.y}</b> ({point.percentage:.1f} %)";
        } else {
          this.tooltipFormat = "{series.name}: <b>{point.percentage:.1f}%</b> ({point.y}). ";
          this.valueFormat = "<b>{point.y}</b> ({point.percentage:.1f}%)";
        }
      }
      // Pie charts - 'pie_only', 'pie_description', 'pie_only_percentage', 'pie_description_percentage'
      else if (this.chartTypeClass === "pie") {
        // If Pie with percentage
        if (this.charttype === "pie_only_percentage") {
          if (this.language == "fr") {
            this.tooltipFormat = "{series.name} : <b>{point.percentage:.1f} %</b> ({point.y}). ";
            this.pieLabelFormat = `{point.name}<br> {series.name} : {point.percentage:.1f} % ({point.y})`;
          } else {
            this.tooltipFormat = "{series.name}: <b>{point.percentage:.1f}%</b> ({point.y}). ";
            this.pieLabelFormat = `{point.name}<br> {series.name}: {point.percentage:.1f}% ({point.y})`;
          }
        } else if (this.charttype === "pie_description_percentage") {
          if (this.language == "fr") {
            this.tooltipFormat = "{series.name} : <b>{point.percentage:.1f} %</b> ({point.y}). ";
            this.pieLabelFormat = null;
          } else {
            this.tooltipFormat = "{series.name}: <b>{point.percentage:.1f}%</b> ({point.y}). ";
            this.pieLabelFormat = `{point.name}<br> {series.name}: {point.percentage:.1f}% ({point.y})`;
          }
        } else if (this.charttype === "pie_description") {
          if (this.language == "fr") {
            this.tooltipFormat = "{series.name} : <b>{point.percentage:.1f} %</b> ({point.y}). ";
            this.pieLabelFormat = null;
          } else {
            this.tooltipFormat = "{series.name}: <b>{point.y}</b>";
            this.pieLabelFormat = null;
          }
        } else {
          this.tooltipFormat = "{series.name}: <b>{point.y}</b>";
          this.pieLabelFormat = `{point.name}<br> {series.name}: <b>{point.y}</b>`;
        }
      } else {
        console.error("Invalid chart type: " + this.charttype);
      }
      this.setHighchartsLanguage();
    },
    setHighchartsLanguage() {
      if (this.language == "fr") {
        console.log("setHighchartsLanguage() try French language");
        Highcharts.setOptions({
          lang: {
            decimalPoint: ",",
            thousandsSep: " ",
          },
        });
        console.log("setHighchartsLanguage() French language set");
      } else {
        console.log("setHighchartsLanguage() try EN language");
        Highcharts.setOptions({
          lang: {
            decimalPoint: ".",
            thousandsSep: ",",
          },
        });
        console.log("setHighchartsLanguage() EN language set");
      }
      this.loadCSVData();
    },

    async loadCSVData() {
      // we use axios to get a promisse. In this case the CSV file.
      console.log("Loading CSV using axios...");

      const maxRetries = 3;
      const retryDelay = 1000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Attempt #${attempt} to load CSV...`);
          const response = await axios.get(this.csvfilepath);
          console.log("CSV loaded successfully!");
          this.rawCsvData = response.data;
          console.log("start renderChart");
          this.renderChart(response.data);
          console.log("finish renderChart");
          return;
        } catch (error) {
          console.error(
            `Error loading chart CSV data: attempt ${attempt}...`,
            error
          );
          if (attempt < maxRetries) {
            console.log(`Retrying in ${retryDelay}ms...`);
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
          } else {
            console.error("All attempts to load chart data failed.");
          }
        }
      }
    },

    renderChart(csvData) {
      if (!csvData || csvData.trim() === "") {
        console.error("CSV data is empty or invalid.");
        return;
      }

      const lines = csvData.split("\n");
      const customPercentageFormat = this.language === "fr" ? " %" : "%";
      let seriesData = [];

      if (this.chartTypeClass === "pie") {
        // For pie charts, we only have one series.
        const series = { name: "", data: [] };

        lines.forEach((line, index) => {
          // Remove trailing commas and split the line.
          const data = line.replace(/,\s*$/, "").split(",");
          // If it's the header row, set the series name (or you could override it with chart title)
          if (index === 0) {
            // Use the second column header as the series name if available.
            series.name = data[1] ? data[1].trim() : this.charttitle;
          } else {
            if (data.length < 2) {
              console.warn(`Skipping malformed row at line ${index + 1}`);
              return;
            }
            const pointName = data[0].trim();
            const pointValue = parseFloat(data[1].trim());
            series.data.push({
              name: pointName,
              y: isNaN(pointValue) ? null : pointValue,
              colour: this.colours[index - 1],
            });
          }
        });

        seriesData.push(series);
        // For pie charts, we don't need categories.
        this.categories = null;
      } else {
        // For column/bar charts (and others) use the existing logic:
        const categories = [];
        seriesData = [];
        lines.forEach((line, index) => {
          const data = line.replace(/,\s*$/, "").split(",");
          if (index === 0) {
            if (data.length < 2) {
              console.error(
                "Invalid CSV format: Header row must contain at least two columns."
              );
              return;
            }
            // Create series for each data column (skip first column for categories)
            for (let i = 1; i < data.length; i++) {
              const series = {
                name: data[i].trim(),
                data: [],
                type: this.chartTypeClass,
              };
              seriesData.push(series);

              // If charttype is "column_grouped_percentage_line" and it's the last iteration, update the last series
              if (
                this.charttype === "column_grouped_percentage_line" &&
                i === data.length - 1
              ) {
                Object.assign(seriesData[seriesData.length - 1], {
                  name: data[i].trim(),
                  data: [],
                  tooltip: {
                    valueSuffix: customPercentageFormat,
                  },
                  label: {
                    enabled: false,
                  },
                  yAxis: 1,
                  marker: {
                    lineWidth: 2,
                    color: "#000000",
                    lineColor: "#000000",
                    fillColor: "white",
                  },
                  pointPlacement: 0,
                  type: "line",
                });
              }
            }
          } else {
            if (data.length !== seriesData.length + 1) {
              console.warn(`Skipping malformed row at line ${index + 1}`);
              return;
            }
            categories.push(data[0].trim());
            for (let i = 1; i < data.length; i++) {
              const value = parseFloat(data[i].trim());
              if (isNaN(value)) {
                console.warn(
                  `Invalid value at line ${index + 1}, column ${i + 1}: "${data[
                    i
                  ].trim()}"`
                );
              }
              seriesData[i - 1].data.push(isNaN(value) ? null : value);
            }
          }
        });
        this.categories = categories;
      }

      this.seriesData = seriesData;

      // Create the chart with Highcharts
      this.chart = Highcharts.chart(this.$refs.chart, {
        chart: {
          // type changes depending on the chart type
          type: this.chartTypeClass,
          // height changes depending on the number of categories for bar charts with more than 4 categories
          ...(this.chartTypeClass === "bar" && this.categories.length > 4
            ? { height: 60 * this.categories.length }
            : {}),
        },
        ...(this.charttype.includes("description") 
          ? { 
              title: null 
            } : {
              title: {
                text: this.charttitle,
                style: {
                  fontSize: "18px",
                },
              },
          }
        ),
        xAxis: {
          categories: this.categories,
          labels: {
            style: {
              fontSize: "14px",
              maxWidth: "100px",
              width: "100%",
            },
          },
          stackLabels: {
            enabled: true,
          },
        },
        yAxis: [
          // Primary yAxis
          {
            // Adjusts the yAxis position to fit the title - for some reason also makes the maxpadding works
            // if charttitle is empty, null or undefined, the top is set to 10
            ...(this.charttitle ? { top: 46 } : { top: 10 }),
            maxPadding: 0,
            title: {
              // making sure the title is not displayed - we don't need it
              text: null,
            },
            labels: {
              //
              ...(this.charttype === "column_percentage_stacked" ||
              this.charttype === "bar_percentage_stacked"
                ? {
                    formatter: function () {
                      return this.value + (customPercentageFormat || "%");
                    },
                  }
                : {}),
              style: { fontSize: "14px" },
            },
            reversedStacks: false,
          },
          // Secondary yAxis for column_grouped_percentage_line
          ...(this.charttype === "column_grouped_percentage_line"
            ? [
                {
                  min: 0,
                  ceiling: 100,
                  maxPadding: 0,
                  stackLabels: { enabled: true },
                  title: { text: null, style: { fontSize: "16px" } },
                  labels: {
                    formatter: function () {
                      return this.value + (customPercentageFormat || "%");
                    },
                    style: { fontSize: "14px" },
                  },
                  opposite: true,
                },
              ]
            : []),
        ],
        legend: {
          reversed: false,
          itemStyle: { fontSize: "14px" },
        },
        tooltip: {
          pointFormat: this.tooltipFormat || "{series.name}: <b>{point.y}</b>. ",
          shared: true,
          style: { fontSize: "14px" },
          hideDelay: 250,
        },
        // Chart options and most of the settings and styles
        plotOptions: {
          series: {
            allowPointSelect: true,
            cursor: "pointer",
            stacking: this.chartSeries.stacking,
            //pie chart plot options series
            ...(this.chartTypeClass === "pie"
              ? {
                  innerSize: "50%", // Donut chart styling
                  dataLabels: [ 
                  {
                    ...(this.charttype.includes("description") 
                    ? { 
                        enabled: false 
                      } : {
                        enabled: true 
                      }
                    ), 
                    distance: 20,
                    format: this.pieLabelFormat,
                    style: { fontSize: "14px" },
                  },
                  {
                    enabled: true,
                    distance: -40,
                    format: "",
                    style: { fontSize: "14px" },
                    filter: {
                      operator: ">",
                      property: "percentage",
                      value: 10,
                    },
                  },
                  ]
                } : 
                // all other charts series options
                {
                  dataLabels: {
                    enabled: true,
                    inside: true,
                    ...(this.charttype === "bar_percentage_stacked"
                      ? {
                          format:
                            this.valueFormat ||
                            "<b>{point.percentage:.1f}%</b> ({point.y})",
                        }
                      : {}),
                    style: { fontSize: "14px" },
                  },
                }),
          },
          column: {
            dataLabels: {
              enabled: true,
              pointFormat:
                this.valueFormat ||
                "<b>{point.percentage:.1f}%</b> ({point.y})",
            },
          },
          line: {
            dataLabels: {
              enabled: true,
              color: "#000",
              format: this.valueFormatSecondAxis,
            },
          },
        },
        colors: this.colours,
        series: seriesData,
        credits: { enabled: false },
      });
    },
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      console.log("Chart destroyed");
    }
  },
};
</script>
